import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from './Button.js';
import { CharacterBubble } from './CharacterBubble.js';
import { Confetti } from './Confetti.js';
import { SlinguAvatar } from './SlinguAvatar.js';
import { evaluatePronunciation, useSpeechRecognition, } from './hooks/useSpeechRecognition.js';
import { useSfx } from './hooks/useSfx.js';
import { useTts } from './hooks/useTts.js';
import { CloseIcon, HeadphonesIcon, HeartIcon, MicIcon, SparkleIcon, XpBoltIcon, } from './icons/index.js';
import { illustrationByKey } from './illustrations/index.js';
import { ProgressBar } from './ProgressBar.js';
const PRAISE = ['Fez bonito!', 'Isso mesmo!', 'Muito bem!', 'Excelente!', 'Mandou bem!'];
const SCOLD = ['Quase lá!', 'Desta vez não...', 'Tente de novo!'];
const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];
function normalize(s) {
    return s.trim().toLowerCase().replace(/\s+/g, ' ');
}
export function LessonPlayer({ exercises, hearts: heartsInit, onClose, onComplete, }) {
    const [idx, setIdx] = useState(0);
    const [correctCount, setCorrectCount] = useState(0);
    const [streak, setStreak] = useState(0);
    const [hearts, setHearts] = useState(heartsInit);
    const [errors, setErrors] = useState([]);
    const [confettiBurst, setConfettiBurst] = useState(0);
    const [feedback, setFeedback] = useState(null);
    // Refs para impedir: (a) múltiplos cliques no Verificar do mesmo exercício
    // contabilizarem XP repetido; (b) o useEffect final disparar onComplete mais
    // de uma vez (dependências instáveis como sfx/onComplete re-renderizam).
    const answeredRef = useRef(false);
    const completedRef = useRef(false);
    const done = idx >= exercises.length;
    const current = exercises[idx];
    const sfx = useSfx();
    useEffect(() => {
        if (!done || completedRef.current)
            return;
        completedRef.current = true;
        sfx.play('level-up');
        const xp = correctCount * 10 + (streak >= 3 ? 15 : 0);
        onComplete({
            correctCount,
            totalCount: exercises.length,
            streak,
            xpEarned: xp,
            errors,
        });
    }, [done, correctCount, streak, exercises.length, errors, onComplete, sfx]);
    if (done || !current)
        return null;
    function check(correct, exId, detail) {
        // Guard: só conta a primeira resposta válida deste exercício. Cliques
        // repetidos no botão Verificar são ignorados até o user avançar.
        if (answeredRef.current)
            return;
        answeredRef.current = true;
        if (correct) {
            sfx.play('correct');
            sfx.play('xp-gain');
            setConfettiBurst((n) => n + 1);
            setCorrectCount((c) => c + 1);
            setStreak((s) => s + 1);
            setFeedback({ correct: true, message: rand(PRAISE), detail });
        }
        else {
            sfx.play('wrong');
            sfx.play('heart-loss');
            setStreak(0);
            setHearts((h) => Math.max(0, h - 1));
            setErrors((e) => [...e, exId]);
            setFeedback({ correct: false, message: rand(SCOLD), detail });
        }
    }
    function next() {
        answeredRef.current = false;
        setFeedback(null);
        setIdx((i) => i + 1);
    }
    return (_jsxs("div", { className: "sl-player", role: "dialog", "aria-label": "Li\u00E7\u00E3o", children: [_jsx(Confetti, { trigger: confettiBurst }), _jsxs("header", { className: "sl-topbar", children: [_jsx("button", { type: "button", className: "sl-topbar__close", onClick: onClose, "aria-label": "Fechar li\u00E7\u00E3o", children: _jsx(CloseIcon, { size: 22 }) }), _jsx(ProgressBar, { value: Math.round((idx / exercises.length) * 100), streak: streak >= 2 ? streak : undefined }), _jsxs("span", { className: "sl-topbar__stat sl-topbar__stat--hearts", children: [_jsx(HeartIcon, { size: 18 }), " ", hearts] })] }), _jsxs("div", { className: "sl-player__body sl-fade-in", children: [_jsx("div", { className: "sl-player-mascot", "aria-hidden": true, children: _jsx(SlinguAvatar, { size: 68, mood: feedback?.correct ? 'happy' : feedback ? 'sad' : 'focus' }) }), _jsx(Challenge, { ex: current, onResult: (correct, detail) => check(correct, current.id, detail) })] }, current.id), feedback ? (_jsxs("div", { className: `sl-feedback sl-feedback--${feedback.correct ? 'correct' : 'wrong'} sl-slide-up`, role: "status", children: [_jsx("div", { className: `sl-feedback-mascot sl-feedback-mascot--${feedback.correct ? 'happy' : 'sad'}`, "aria-hidden": true, children: _jsx(SlinguAvatar, { size: 64, mood: feedback.correct ? 'happy' : 'sad', wings: false }) }), _jsx("h4", { children: feedback.message }), feedback.detail ? _jsx("p", { className: "sl-feedback__detail", children: feedback.detail }) : null, _jsx("div", { className: "sl-player__actions", children: _jsx(Button, { variant: feedback.correct ? 'primary' : 'danger', size: "lg", onClick: next, children: "Continuar" }) })] })) : null, confettiBurst > 0 && feedback?.correct ? (_jsx("span", { className: "sl-xp-pop", children: "+10 XP" }, `xp-${confettiBurst}`)) : null] }));
}
function Challenge({ ex, onResult, }) {
    const tag = ex.newWord ? (_jsxs("span", { className: "sl-tag", children: [_jsx(SparkleIcon, { size: 14 }), "PALAVRA NOVA ", ex.newWord ? `· ${ex.newWord}` : ''] })) : null;
    const character = ex.character && ex.bubble ? (_jsx(CharacterBubble, { name: ex.character.name, color: ex.character.color, initials: ex.character.initials, keyword: ex.keyword, children: ex.bubble })) : null;
    switch (ex.type) {
        case 'multiple_choice':
            return (_jsxs("div", { className: "sl-exercise", children: [tag, _jsx("h2", { className: "sl-exercise__prompt", children: ex.prompt }), character, _jsx(MultipleChoiceBody, { choices: ex.choices, answerIndex: ex.answerIndex, onResult: onResult })] }));
        case 'fill_blank':
            return (_jsxs("div", { className: "sl-exercise", children: [tag, _jsx("h2", { className: "sl-exercise__prompt", children: ex.prompt }), character, _jsx(TextAnswerBody, { accepted: [ex.answer, ...(ex.accepts ?? [])], onResult: onResult })] }));
        case 'translate':
            return (_jsxs("div", { className: "sl-exercise", children: [tag, _jsxs("p", { className: "sl-exercise__label", children: ["Escreva em ", ex.to === 'en' ? 'inglês' : 'português'] }), _jsx("h2", { className: "sl-exercise__prompt", children: ex.prompt }), character, _jsx(TextAnswerBody, { accepted: [ex.answer, ...(ex.accepts ?? [])], onResult: onResult })] }));
        case 'reorder':
            return (_jsxs("div", { className: "sl-exercise", children: [tag, _jsx("h2", { className: "sl-exercise__prompt", children: ex.prompt }), character, _jsx(ReorderBody, { tokens: ex.tokens, answer: ex.answer, onResult: onResult })] }));
        case 'listen':
            return _jsx(ListenBody, { ex: ex, voiceId: ex.voiceId, onResult: onResult, tag: tag });
        case 'listen_choice':
            return _jsx(ListenChoiceBody, { ex: ex, voiceId: ex.voiceId, onResult: onResult, tag: tag });
        case 'image_select':
            return _jsx(ImageSelectBody, { ex: ex, voiceId: ex.voiceId, onResult: onResult, tag: tag });
        case 'speak':
            return _jsx(SpeakBody, { ex: ex, voiceId: ex.voiceId, onResult: onResult, tag: tag });
        case 'match_pairs':
            return (_jsxs("div", { className: "sl-exercise", children: [_jsx("h2", { className: "sl-exercise__prompt", children: ex.prompt }), _jsx(MatchPairsBody, { pairs: ex.pairs, onResult: onResult })] }));
        default:
            return _jsx("div", { className: "sl-exercise", children: "Tipo desconhecido." });
    }
}
function ImageSelectBody({ ex, voiceId, onResult, tag, }) {
    const { play, state } = useTts();
    const [selected, setSelected] = useState(null);
    const playedRef = useRef(null);
    // Auto-play prompt audio once per exercise id.
    useEffect(() => {
        if (playedRef.current === ex.id)
            return;
        playedRef.current = ex.id;
        void play(ex.promptAudio, voiceId);
    }, [ex.id, ex.promptAudio, play, voiceId]);
    function tap(i) {
        setSelected(i);
        const opt = ex.options[i];
        if (opt)
            void play(opt.audio ?? opt.word, voiceId);
    }
    return (_jsxs("div", { className: "sl-exercise", children: [tag, _jsx("p", { className: "sl-exercise__label", children: ex.prompt }), _jsxs("button", { type: "button", className: `sl-btn sl-btn--secondary${state === 'playing' ? ' sl-pulse' : ''}`, onClick: () => play(ex.promptAudio, voiceId), children: [_jsx(HeadphonesIcon, { size: 22 }), " Ouvir pergunta"] }), _jsx("ul", { className: "sl-image-grid", role: "radiogroup", children: ex.options.map((opt, i) => {
                    const Illus = illustrationByKey(opt.illustration);
                    const isSelected = selected === i;
                    return (_jsx("li", { children: _jsxs("button", { type: "button", role: "radio", "aria-checked": isSelected, className: `sl-image-tile${isSelected ? ' sl-image-tile--selected' : ''}`, onClick: () => tap(i), children: [_jsx("span", { className: "sl-image-tile__art", "aria-hidden": true, children: _jsx(Illus, { size: 64 }) }), _jsx("span", { className: "sl-image-tile__word", children: opt.word })] }) }, opt.word));
                }) }), _jsx(Button, { size: "lg", disabled: selected === null, onClick: () => onResult(selected === ex.answerIndex, `Resposta: ${ex.options[ex.answerIndex]?.word ?? ''}`), children: "Verificar" })] }));
}
function MultipleChoiceBody({ choices, answerIndex, onResult, }) {
    const [selected, setSelected] = useState(null);
    return (_jsxs(_Fragment, { children: [_jsx("ul", { className: "sl-choices", children: choices.map((c, i) => (_jsx("li", { children: _jsx("button", { type: "button", className: `sl-choice${selected === i ? ' sl-choice--selected' : ''}`, onClick: () => setSelected(i), children: _jsx("span", { className: "sl-choice__label", children: c }) }) }, c))) }), _jsx(Button, { size: "lg", disabled: selected === null, onClick: () => onResult(selected === answerIndex, `Resposta correta: ${choices[answerIndex]}`), children: "Verificar" })] }));
}
function TextAnswerBody({ accepted, onResult, }) {
    const [value, setValue] = useState('');
    const normAccepted = useMemo(() => accepted.map(normalize), [accepted]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "sl-input-row", children: _jsx("input", { className: "sl-input", value: value, onChange: (e) => setValue(e.target.value), "aria-label": "Sua resposta", autoComplete: "off", spellCheck: false }) }), _jsx(Button, { size: "lg", disabled: value.trim().length === 0, onClick: () => onResult(normAccepted.includes(normalize(value)), `Resposta: ${accepted[0]}`), children: "Verificar" })] }));
}
function ReorderBody({ tokens, answer, onResult, }) {
    const pool = useMemo(() => tokens.map((t, i) => ({ t, i })).sort(() => Math.random() - 0.5), [tokens]);
    const [bank, setBank] = useState(pool);
    const [placed, setPlaced] = useState([]);
    function place(item) {
        setBank((b) => b.filter((x) => x.i !== item.i));
        setPlaced((p) => [...p, item]);
    }
    function take(item) {
        setPlaced((p) => p.filter((x) => x.i !== item.i));
        setBank((b) => [...b, item]);
    }
    const attempt = placed.map((p) => p.t);
    const solved = attempt.length === answer.length && attempt.every((t, i) => t === answer[i]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "sl-token-slots", role: "list", children: placed.map((it) => (_jsx("button", { className: "sl-token sl-token--placed", onClick: () => take(it), children: _jsx("span", { children: it.t }) }, it.i))) }), _jsx("ul", { className: "sl-tokens", children: bank.map((it) => (_jsx("li", { children: _jsx("button", { className: "sl-token", onClick: () => place(it), children: _jsx("span", { children: it.t }) }) }, it.i))) }), _jsx(Button, { size: "lg", disabled: placed.length === 0, onClick: () => onResult(solved, `Resposta: ${answer.join(' ')}`), children: "Verificar" })] }));
}
function ListenBody({ ex, voiceId, onResult, tag, }) {
    const { play, state } = useTts();
    const playedRef = useRef(null);
    useEffect(() => {
        if (playedRef.current === ex.id)
            return;
        playedRef.current = ex.id;
        void play(ex.audioText, voiceId);
    }, [ex.id, ex.audioText, play, voiceId]);
    return (_jsxs("div", { className: "sl-exercise", children: [tag, _jsx("p", { className: "sl-exercise__label", children: "Ou\u00E7a e digite" }), _jsxs("button", { type: "button", className: `sl-btn sl-btn--secondary${state === 'playing' ? ' sl-pulse' : ''}`, onClick: () => play(ex.audioText, voiceId), "aria-label": "Reproduzir \u00E1udio", children: [_jsx(HeadphonesIcon, { size: 24 }), " Ouvir novamente"] }), _jsx(TextAnswerBody, { accepted: [ex.answer], onResult: onResult })] }));
}
function ListenChoiceBody({ ex, voiceId, onResult, tag, }) {
    const { play, state } = useTts();
    const [selected, setSelected] = useState(null);
    const playedRef = useRef(null);
    useEffect(() => {
        if (playedRef.current === ex.id)
            return;
        playedRef.current = ex.id;
        void play(ex.audioText, voiceId);
    }, [ex.id, ex.audioText, play, voiceId]);
    return (_jsxs("div", { className: "sl-exercise", children: [tag, _jsx("p", { className: "sl-exercise__label", children: ex.prompt }), _jsxs("button", { type: "button", className: `sl-btn sl-btn--secondary${state === 'playing' ? ' sl-pulse' : ''}`, onClick: () => play(ex.audioText, voiceId), children: [_jsx(HeadphonesIcon, { size: 24 }), " Ouvir novamente"] }), _jsx("ul", { className: "sl-choices", children: ex.choices.map((c, i) => (_jsx("li", { children: _jsx("button", { type: "button", className: `sl-choice${selected === i ? ' sl-choice--selected' : ''}`, onClick: () => setSelected(i), children: _jsx("span", { className: "sl-choice__label", children: c }) }) }, c))) }), _jsx(Button, { size: "lg", disabled: selected === null, onClick: () => onResult(selected === ex.answerIndex, `Você ouviu: "${ex.choices[ex.answerIndex]}"`), children: "Verificar" })] }));
}
function SpeakBody({ ex, voiceId, onResult, tag, }) {
    const { play } = useTts();
    void voiceId;
    const { transcribe, state: recState, error: recError } = useSpeechRecognition('en-US');
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const hasMicRef = useRef(false);
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        hasMicRef.current = !!(window.SpeechRecognition ?? window.webkitSpeechRecognition);
    }, []);
    async function record() {
        setBusy(true);
        setResult(null);
        try {
            const transcript = await transcribe();
            const evalResult = await evaluatePronunciation(ex.target, transcript);
            setResult(evalResult);
        }
        catch (err) {
            setResult({
                similarity: 0,
                grade: 'retry',
                diff: [],
                target: ex.target,
                transcript: '',
            });
            console.warn('pronunciation error', err);
        }
        finally {
            setBusy(false);
        }
    }
    const canSpeak = hasMicRef.current;
    return (_jsxs("div", { className: "sl-exercise", children: [tag, _jsx("p", { className: "sl-exercise__label", children: "Repita em voz alta" }), _jsx("h2", { className: "sl-exercise__prompt", children: ex.target }), _jsxs("button", { type: "button", className: "sl-btn sl-btn--secondary", onClick: () => play(ex.target, voiceId), children: [_jsx(HeadphonesIcon, { size: 22 }), " Ouvir pron\u00FAncia"] }), result ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: `sl-feedback sl-feedback--${result.grade === 'good' || result.grade === 'okay' ? 'correct' : 'wrong'}`, children: [_jsxs("h4", { children: [result.grade === 'good'
                                        ? 'Pronúncia excelente'
                                        : result.grade === 'okay'
                                            ? 'Pronúncia boa'
                                            : 'Tente novamente', ' ', "\u2014 ", result.similarity, "%"] }), result.diff.length > 0 ? (_jsx("p", { className: "sl-feedback__detail", children: result.diff.map((w, i) => (_jsx("span", { style: {
                                        color: w.ok ? 'var(--sl-accent-shade)' : 'var(--sl-error)',
                                        marginRight: 6,
                                    }, children: w.word }, `${w.word}-${i}`))) })) : null, result.transcript ? (_jsxs("p", { className: "sl-feedback__detail", children: ["Voc\u00EA disse: \"", result.transcript, "\""] })) : null] }), _jsx(Button, { size: "lg", onClick: () => onResult(result.grade !== 'retry', `Alvo: ${ex.target}`), children: result.grade === 'retry' ? 'Prosseguir (sem XP)' : 'Continuar' })] })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "sl-speak", children: [_jsx("button", { type: "button", className: `sl-btn sl-btn--primary sl-speak__button${recState === 'listening' ? ' sl-pulse' : ''}`, onClick: () => void record(), disabled: busy || !canSpeak, "aria-label": recState === 'listening' ? 'Gravando…' : 'Gravar', children: _jsx(MicIcon, { size: 32 }) }), _jsx("p", { className: "sl-speak__hint", children: canSpeak
                                    ? recState === 'listening'
                                        ? 'Ouvindo…'
                                        : 'Toque e leia em voz alta'
                                    : 'Microfone indisponível neste navegador. Use Chrome/Edge.' }), recError ? (_jsxs("p", { style: { color: 'var(--sl-error)', fontSize: 13 }, children: ["Erro: ", recError] })) : null] }), !canSpeak ? (_jsx(Button, { size: "lg", variant: "secondary", onClick: () => onResult(true), children: "Pular pron\u00FAncia" })) : null] }))] }));
}
function MatchPairsBody({ pairs, onResult, }) {
    const left = pairs.map((p, i) => ({ label: p.left, id: `L${i}`, pairId: i }));
    const right = useMemo(() => pairs
        .map((p, i) => ({ label: p.right, id: `R${i}`, pairId: i }))
        .sort(() => Math.random() - 0.5), [pairs]);
    const [selL, setSelL] = useState(null);
    const [selR, setSelR] = useState(null);
    const [matched, setMatched] = useState(new Set());
    const [wrongFlash, setWrongFlash] = useState(null);
    function clickL(pid) {
        if (matched.has(pid))
            return;
        if (selR !== null) {
            if (selR === pid) {
                setMatched((m) => new Set([...m, pid]));
                setSelR(null);
            }
            else {
                setWrongFlash({ l: pid, r: selR });
                setTimeout(() => setWrongFlash(null), 400);
                setSelR(null);
            }
        }
        else {
            setSelL(pid);
        }
    }
    function clickR(pid) {
        if (matched.has(pid))
            return;
        if (selL !== null) {
            if (selL === pid) {
                setMatched((m) => new Set([...m, pid]));
                setSelL(null);
            }
            else {
                setWrongFlash({ l: selL, r: pid });
                setTimeout(() => setWrongFlash(null), 400);
                setSelL(null);
            }
        }
        else {
            setSelR(pid);
        }
    }
    const allMatched = matched.size === pairs.length;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "sl-match", children: [_jsx("div", { className: "sl-match__col", children: left.map((it) => {
                            const isMatched = matched.has(it.pairId);
                            const isSel = selL === it.pairId;
                            const isWrong = wrongFlash?.l === it.pairId;
                            return (_jsx("button", { className: `sl-choice${isSel ? ' sl-choice--selected' : ''}${isMatched ? ' sl-choice--correct' : ''}${isWrong ? ' sl-choice--wrong' : ''}`, disabled: isMatched, onClick: () => clickL(it.pairId), children: it.label }, it.id));
                        }) }), _jsx("div", { className: "sl-match__col", children: right.map((it) => {
                            const isMatched = matched.has(it.pairId);
                            const isSel = selR === it.pairId;
                            const isWrong = wrongFlash?.r === it.pairId;
                            return (_jsx("button", { className: `sl-choice${isSel ? ' sl-choice--selected' : ''}${isMatched ? ' sl-choice--correct' : ''}${isWrong ? ' sl-choice--wrong' : ''}`, disabled: isMatched, onClick: () => clickR(it.pairId), children: it.label }, it.id));
                        }) })] }), _jsx(Button, { size: "lg", disabled: !allMatched, onClick: () => onResult(true), children: "Continuar" })] }));
}
export function LessonSummary({ stats, onDone, }) {
    return (_jsxs("div", { className: "sl-player", children: [_jsxs("header", { className: "sl-topbar", children: [_jsx("div", {}), _jsx("div", { style: { textAlign: 'center', fontWeight: 700 }, children: "Li\u00E7\u00E3o conclu\u00EDda" }), _jsx("div", {})] }), _jsx("div", { className: "sl-player__body sl-fade-in", children: _jsxs("div", { className: "sl-section--grid", children: [_jsx(StatCard, { icon: _jsx(XpBoltIcon, { size: 28 }), label: "XP ganho", value: stats.xpEarned, color: "rgb(73, 192, 248)" }), _jsx(StatCard, { icon: _jsx(SparkleIcon, { size: 28 }), label: "Acertos", value: `${stats.correctCount}/${stats.totalCount}`, color: "rgb(147, 211, 51)" }), _jsx(StatCard, { icon: _jsx(HeartIcon, { size: 28 }), label: "Sequ\u00EAncia", value: `${stats.streak} seguidos`, color: "rgb(255, 171, 51)" })] }) }), _jsx("div", { className: "sl-player__footer", children: _jsx("div", { className: "sl-player__actions", children: _jsx(Button, { size: "lg", onClick: onDone, children: "Continuar" }) }) })] }));
}
function StatCard({ icon, label, value, color, }) {
    return (_jsxs("div", { className: "sl-mission", style: { borderColor: color }, children: [_jsxs("div", { style: { color, display: 'flex', gap: 8, alignItems: 'center' }, children: [icon, _jsx("strong", { style: { fontSize: 28 }, children: value })] }), _jsx("p", { style: { color: 'var(--sl-text-muted)', margin: 0, fontSize: 14 }, children: label })] }));
}
//# sourceMappingURL=LessonPlayer.js.map